import {
  type QuotePriceQuantityViolation,
  validateQuotePriceQuantities,
} from '@calculations/feature/alpha/utilities/validate-quote-price-quantities';
import type {
  FieldError,
  FieldValidator,
  LineItemFieldKey,
  QuantityBreakFieldKey,
  Value,
  ValueId,
} from '@snap-types/models/auto-configurator/state';
import { Decimal } from '@snap-types/types/decimal';

type FieldKeyByModel = {
  lineItem: LineItemFieldKey;
  quantityBreak: QuantityBreakFieldKey;
};

interface ConditionalRequiredOptions {
  message?: string;
}

export function validateQuantityGreaterThanPreviousQuantityBreak(
  message = 'Quantity must be greater than previous quantity break',
): FieldValidator {
  return (context) => {
    if (!isQuantityBreakContext(context)) {
      return [];
    }

    const isViolating = getLineQuantityViolations(context).some(
      (violation) => violation.uuid === context.valueId.uuid,
    );

    return isViolating ? [{ message } satisfies FieldError] : [];
  };
}

export function validateQuantityLessThanNextQuantityBreak(
  message = 'Quantity must be less than next quantity break',
): FieldValidator {
  return (context) => {
    if (!isQuantityBreakContext(context)) {
      return [];
    }

    const isViolating = getLineQuantityViolations(context).some(
      (violation) => violation.previousUuid === context.valueId.uuid,
    );

    return isViolating ? [{ message } satisfies FieldError] : [];
  };
}

export function validateQuantityGreaterThanOrEqualToMinimumQuantity(
  message = 'Quantity must be greater than or equal to minimum quantity',
): FieldValidator {
  return (context) => {
    if (!isQuantityBreakContext(context)) {
      return [];
    }

    const currentQuantity = getNonNullDecimal(context.value);

    if (currentQuantity === null) {
      return [];
    }

    const quantityBreak = context.state.quantityBreaks[context.valueId.uuid];

    if (!quantityBreak) {
      return [];
    }

    const minimumQuantity =
      context.state.lineItems[quantityBreak.lineItemUuid]?.calculatedData
        ?.minimumQuantity;

    if (
      !minimumQuantity ||
      currentQuantity.greaterThanOrEqualTo(minimumQuantity)
    ) {
      return [];
    }

    return [
      {
        message: `${message} ${minimumQuantity.toString()}`,
      } satisfies FieldError,
    ];
  };
}

export function requiredWhenSameRowValuePresent(
  model: keyof FieldKeyByModel,
  otherKey: FieldKeyByModel[keyof FieldKeyByModel],
  options: ConditionalRequiredOptions = {},
): FieldValidator {
  return (context) => {
    if (context.valueId.model !== model) {
      return [];
    }

    const otherValue = getSameRowValue(
      context.state,
      context.valueId,
      otherKey,
    );

    if (!isValuePresent(otherValue)) {
      return [];
    }

    if (isValuePresent(context.value)) {
      return [];
    }

    return [{ message: options.message ?? 'Required' }];
  };
}

export function requiredWhenLineItemValuePresentForQuantityBreak(
  lineItemKey: LineItemFieldKey,
  options: ConditionalRequiredOptions = {},
): FieldValidator {
  return (context) => {
    if (context.valueId.model !== 'quantityBreak') {
      return [];
    }

    const quantityBreak = context.state.quantityBreaks[context.valueId.uuid];

    if (!quantityBreak) {
      return [];
    }

    const lineItemValue = context.getValue({
      model: 'lineItem',
      uuid: quantityBreak.lineItemUuid,
      key: lineItemKey,
    });

    if (!isValuePresent(lineItemValue)) {
      return [];
    }

    if (isValuePresent(context.value)) {
      return [];
    }

    return [{ message: options.message ?? 'Required' }];
  };
}

function getSameRowValue(
  state: Parameters<FieldValidator>[0]['state'],
  valueId: ValueId,
  otherKey: FieldKeyByModel[keyof FieldKeyByModel],
): Value | null {
  if (valueId.model === 'lineItem') {
    const lineItem = state.lineItems[valueId.uuid];

    if (!lineItem) {
      return null;
    }

    return lineItem[otherKey as LineItemFieldKey];
  }

  if (valueId.model === 'quantityBreak') {
    const quantityBreak = state.quantityBreaks[valueId.uuid];

    if (!quantityBreak) {
      return null;
    }

    return quantityBreak[otherKey as QuantityBreakFieldKey];
  }

  return null;
}

/**
 * Runs the shared ordering rule over the whole line so both the forward and
 * backward validators read from one implementation, rather than each
 * reimplementing the comparison.
 */
function getLineQuantityViolations(
  context: Parameters<FieldValidator>[0],
): QuotePriceQuantityViolation[] {
  if (!isQuantityBreakContext(context)) {
    return [];
  }

  const quantityBreak = context.state.quantityBreaks[context.valueId.uuid];

  if (!quantityBreak) {
    return [];
  }

  const lineItem = context.state.lineItems[quantityBreak.lineItemUuid];

  if (!lineItem) {
    return [];
  }

  return validateQuotePriceQuantities(
    lineItem.quantityBreakUuids.map((uuid) => ({
      uuid,
      quantity: getNonNullDecimal(
        context.state.quantityBreaks[uuid]?.quantity ?? null,
      ),
    })),
  );
}

function isQuantityBreakContext(
  context: Parameters<FieldValidator>[0],
): context is Parameters<FieldValidator>[0] & {
  valueId: {
    model: 'quantityBreak';
    uuid: string;
    key: QuantityBreakFieldKey;
  };
} {
  return context.valueId.model === 'quantityBreak';
}

function getNonNullDecimal(value: Value | null): Decimal | null {
  if (!value || value.type !== 'decimal' || value.value === null) {
    return null;
  }

  return value.value;
}

function isValuePresent(value: Value | null): boolean {
  if (!value) {
    return false;
  }

  switch (value.type) {
    case 'number':
    case 'decimal':
      return value.value !== null;

    case 'select':
      return value.value !== null && value.value.value.trim() !== '';

    case 'text':
      return value.value !== null && value.value.trim() !== '';

    default:
      return false;
  }
}
